import { useCallback } from 'react';

import i18n from '@/settings/i18n';
import { userListApi } from '@/services/api/userList';
import { User } from '@/types/userList';

import * as Sentry from '@sentry/react';
import axios from 'axios';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';

type Props = {
  organizationId: number;
  refreshPage: () => void;
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>;
  users: User[] | null;
};

export const useBulkUpdate = ({
  organizationId,
  refreshPage,
  setIsUpdating,
  users,
}: Props) => {
  const { apiErrorHandler } = useApiErrorHandler();
  const { flashNotice } = useFlashMessenger();
  const bulkUpdate = useCallback<
    (e: React.MouseEvent<HTMLButtonElement>) => Promise<void>
  >(
    async (e) => {
      e.preventDefault();

      if (!users) {
        return;
      }

      try {
        setIsUpdating(true);

        await userListApi.updateUsers(organizationId, users);

        flashNotice(i18n.t('text.user.update.done'));

        await refreshPage();
      } catch (err) {
        if (
          axios.isAxiosError(err) &&
          err.response &&
          err.response.status === 401
        ) {
          try {
            localStorage.clear();
          } catch (e) {
            Sentry.captureException(e);
          }
        }

        apiErrorHandler(err);

        await refreshPage();
      } finally {
        setIsUpdating(false);
      }
    },
    [
      users,
      setIsUpdating,
      organizationId,
      flashNotice,
      refreshPage,
      apiErrorHandler,
    ],
  );

  return { bulkUpdate };
};
